import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  FileText, Video, Upload, Download, Trash2, Plus, Link, 
  Image, File, ExternalLink, Eye, X, FolderOpen
} from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const FILE_ICONS = {
  documents: FileText,
  images: Image,
  videos: Video,
  other: File
};

export default function DocumentsVideos({ childId, childName }) {
  const [documents, setDocuments] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('documents');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [videoForm, setVideoForm] = useState({
    title: '',
    url: '',
    description: '',
    video_type: 'performance'
  });

  const loadData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [docsRes, videosRes] = await Promise.all([
        axios.get(`${API}/children/${childId}/documents`, { headers }),
        axios.get(`${API}/children/${childId}/videos`, { headers })
      ]);
      
      setDocuments(docsRes.data.documents || []);
      setVideos(videosRes.data.videos || []);
    } catch (error) {
      console.error('Failed to load media:', error);
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFileUpload = async () => {
    if (!uploadFile) return;
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', uploadFile);
      if (uploadDescription) {
        formData.append('description', uploadDescription);
      }
      
      await axios.post(`${API}/children/${childId}/documents`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Document uploaded successfully');
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadDescription('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleAddVideo = async () => {
    if (!videoForm.title || !videoForm.url) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/children/${childId}/videos`, videoForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Video link added successfully');
      setShowVideoModal(false);
      setVideoForm({ title: '', url: '', description: '', video_type: 'performance' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add video link');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/children/${childId}/documents/${doc.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/children/${childId}/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to remove this video link?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/children/${childId}/videos/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Video link removed');
      loadData();
    } catch (error) {
      toast.error('Failed to remove video link');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getVideoEmbedUrl = (url) => {
    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
    
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#02b5ff]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="documents-videos">
      {/* Header with tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'documents' ? 'default' : 'outline'}
            onClick={() => setActiveTab('documents')}
            size="sm"
            data-testid="tab-documents"
          >
            <FileText className="h-4 w-4 mr-2" />
            Documents ({documents.length})
          </Button>
          <Button
            variant={activeTab === 'videos' ? 'default' : 'outline'}
            onClick={() => setActiveTab('videos')}
            size="sm"
            data-testid="tab-videos"
          >
            <Video className="h-4 w-4 mr-2" />
            Videos ({videos.length})
          </Button>
        </div>
        
        <div className="flex gap-2">
          {activeTab === 'documents' ? (
            <Button 
              onClick={() => setShowUploadModal(true)}
              size="sm"
              className="bg-[#02b5ff] hover:bg-[#0099dd]"
              data-testid="upload-document-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Document
            </Button>
          ) : (
            <Button 
              onClick={() => setShowVideoModal(true)}
              size="sm"
              className="bg-[#02b5ff] hover:bg-[#0099dd]"
              data-testid="add-video-btn"
            >
              <Link className="h-4 w-4 mr-2" />
              Add Video Link
            </Button>
          )}
        </div>
      </div>

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className="space-y-4">
          {documents.length === 0 ? (
            <div className="text-center py-12 bg-zinc-900/30 rounded-lg border border-zinc-800">
              <FolderOpen className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-500">No documents uploaded yet</p>
              <Button 
                onClick={() => setShowUploadModal(true)}
                variant="outline"
                className="mt-4"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload First Document
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {documents.map(doc => {
                const IconComponent = FILE_ICONS[doc.category] || File;
                return (
                  <div 
                    key={doc.id}
                    className="bg-zinc-900/30 border border-zinc-800 rounded-lg p-4 hover:border-zinc-700 transition-colors"
                    data-testid={`document-${doc.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-[#02b5ff]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="h-5 w-5 text-[#02b5ff]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate" title={doc.original_filename}>
                          {doc.original_filename}
                        </p>
                        <p className="text-xs text-zinc-500">
                          {formatFileSize(doc.file_size)} • {new Date(doc.uploaded_at).toLocaleDateString()}
                        </p>
                        {doc.description && (
                          <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{doc.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(doc)}
                        className="flex-1 text-xs"
                      >
                        <Download className="h-3 w-3 mr-1" />
                        Download
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteDocument(doc.id)}
                        className="text-[#ff025f] hover:text-[#ff025f] hover:border-[#ff025f]"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Videos Tab */}
      {activeTab === 'videos' && (
        <div className="space-y-4">
          {videos.length === 0 ? (
            <div className="text-center py-12 bg-zinc-900/30 rounded-lg border border-zinc-800">
              <Video className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-500">No video links added yet</p>
              <Button 
                onClick={() => setShowVideoModal(true)}
                variant="outline"
                className="mt-4"
              >
                <Link className="h-4 w-4 mr-2" />
                Add First Video
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {videos.map(video => {
                const embedUrl = getVideoEmbedUrl(video.url);
                return (
                  <div 
                    key={video.id}
                    className="bg-zinc-900/30 border border-zinc-800 rounded-lg overflow-hidden"
                    data-testid={`video-${video.id}`}
                  >
                    {embedUrl ? (
                      <div className="aspect-video bg-black">
                        <iframe
                          src={embedUrl}
                          className="w-full h-full"
                          allowFullScreen
                          title={video.title}
                        />
                      </div>
                    ) : (
                      <div className="aspect-video bg-zinc-800 flex items-center justify-center">
                        <a 
                          href={video.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex flex-col items-center gap-2 text-zinc-400 hover:text-white"
                        >
                          <ExternalLink className="h-8 w-8" />
                          <span className="text-sm">Open in new tab</span>
                        </a>
                      </div>
                    )}
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h4 className="font-medium text-white">{video.title}</h4>
                          <span className="inline-block px-2 py-0.5 bg-[#02b5ff]/20 text-[#02b5ff] text-xs rounded mt-1 capitalize">
                            {video.video_type}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          <a
                            href={video.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 hover:bg-zinc-800 rounded"
                          >
                            <ExternalLink className="h-4 w-4 text-zinc-400" />
                          </a>
                          <button
                            onClick={() => handleDeleteVideo(video.id)}
                            className="p-2 hover:bg-[#ff025f]/20 rounded text-[#ff025f]"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      {video.description && (
                        <p className="text-sm text-zinc-400 mt-2">{video.description}</p>
                      )}
                      <p className="text-xs text-zinc-500 mt-2">
                        Added {new Date(video.added_at).toLocaleDateString()} by {video.added_by_name}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Upload Document Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Upload Document</h3>
              <button onClick={() => setShowUploadModal(false)} className="text-zinc-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-zinc-300">Select File</Label>
                <Input
                  type="file"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  accept=".pdf,.doc,.docx,.txt,.xlsx,.xls,.csv,.jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.avi,.webm"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Max 50MB. Supported: PDF, DOC, images, videos
                </p>
              </div>
              
              <div>
                <Label className="text-zinc-300">Description (optional)</Label>
                <Input
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  placeholder="Brief description of the document"
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleFileUpload}
                  disabled={!uploadFile || uploading}
                  className="flex-1 bg-[#02b5ff] hover:bg-[#0099dd]"
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Video Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Add Video Link</h3>
              <button onClick={() => setShowVideoModal(false)} className="text-zinc-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-zinc-300">Title *</Label>
                <Input
                  value={videoForm.title}
                  onChange={(e) => setVideoForm({...videoForm, title: e.target.value})}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  placeholder="e.g., Sprint Training Session"
                />
              </div>
              
              <div>
                <Label className="text-zinc-300">Video URL *</Label>
                <Input
                  value={videoForm.url}
                  onChange={(e) => setVideoForm({...videoForm, url: e.target.value})}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>
              
              <div>
                <Label className="text-zinc-300">Video Type</Label>
                <Select 
                  value={videoForm.video_type} 
                  onValueChange={(v) => setVideoForm({...videoForm, video_type: v})}
                >
                  <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="performance">Performance Analysis</SelectItem>
                    <SelectItem value="training">Training Session</SelectItem>
                    <SelectItem value="competition">Competition</SelectItem>
                    <SelectItem value="highlight">Highlight Reel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-zinc-300">Description (optional)</Label>
                <Input
                  value={videoForm.description}
                  onChange={(e) => setVideoForm({...videoForm, description: e.target.value})}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  placeholder="Brief description"
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowVideoModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddVideo}
                  disabled={!videoForm.title || !videoForm.url}
                  className="flex-1 bg-[#02b5ff] hover:bg-[#0099dd]"
                >
                  Add Video
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
